import { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { elementUnderMouse } from '../../utils/dom.js';
import { PREVENT_EVENT_CAPTURE, stopPropagation, preventDefault, isPortaledEvent, targetIsCurrent } from '../../utils/event.js';
import { noop } from '../../utils/fn.js';
import { isFocusWithin, blurActiveElement } from '../../utils/focus.js';
import { mergeProps } from '../../utils/merge-props.js';
import { expandRect } from '../../utils/rect.js';
import { containsSelectedText, isTextInputWithSelection } from '../../utils/selection.js';
import { ZERO, fromClientXY, subtract, magnitude, distanceToElement, exitPoint, distance, normalize, add } from '../../utils/vector.js';
import { getFurthestEditableAncestorOrSelf, getDeadZone, MOVEMENT_EVENTS_OUTSIDE_TEXT_INPUT, TOUCH_TIMEOUT } from './use-drag.utils.js';
import { base, dragging, draggingBody } from './use-drag.css.js';

// NOTE: useDrag is just an alias for useCancellableDrag but with a different type signature.
// A cancellable drag's `onDragEnd` can be called with a `{ cancelled: true }` object with no other
// properties, which might make it hard for callers to use. If a drag won't be cancelled, then the
// `onDragEnd` event will always be a `PointerEvent` with a `delta` property. To make it easier on
// callers, we export `useDrag` as a separate function with a different type signature.
/**
 * Return a tuple of [dragging, getDragProps]. `dragging` will be `true` while the user is dragging.
 * `getDragProps` can be called with additional props to merge together, such as a className,
 * and should be applied to the element that you wish to enable dragging on.
 *
 * The object returned by getDragProps should be considered opaque, since we may change the
 * implementation in the future and want to be able to do so without breaking users.
 *
 * @see {@link https://proto.figma.design/fpl-storybook/master/?path=/docs/hooks-usedrag--docs}
 */
const useDrag = useCancellableDrag;
/**
 * Return a tuple of [dragging, getDragProps]. `dragging` will be `true` while the user is dragging.
 * `getDragProps` can be called with additional props to merge together, such as a className,
 * and should be applied to the element that you wish to enable dragging on.
 *
 * This is a cancellable version of `useDrag` that allows the caller to cancel the drag before
 * the user has released the pointer. To set up a cancellable drag, the caller should register an
 * AbortSignal in `onDragStart`, and then may trigger the AbortSignal at any time.
 *
 * The object returned by getDragProps should be considered opaque, since we may change the
 * implementation in the future and want to be able to do so without breaking users.
 *
 * @see {@link https://proto.figma.design/fpl-storybook/master/?path=/docs/hooks-usedrag--docs}
 */
function useCancellableDrag(ctx) {
    const start = useRef(ZERO);
    const pointerId = useRef(null);
    const pointerCaptureEl = useRef(null);
    const pendingPointerCaptureEl = useRef(null);
    const pointerDownStartTime = useRef(0);
    const dragEndRef = useRef(noop);
    const initialTextInput = useRef(null);
    const movementEventsOutsideTextInput = useRef(0);
    // This is mostly to trigger a rerender when the drag starts and ends, but shouldn't be relied on
    // within this hook since it will update async. Internally we can check if pointerId is non-null.
    const [dragging$1, setDragging] = useState(false);
    useEffect(() => {
        return () => {
            // If we're unmounting but we're still mid-drag, cancel the drag so that we don't
            // end up in a stuck state
            if (pointerId.current != null) {
                dragEndRef.current(null, true);
            }
        };
    }, []);
    // Cast to any because mergeProps called with no arguments returns an `object` type, which
    // has no index signature compatible with PropBag's DataAttrs signature, but it's fine at runtime
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    if (ctx.disabled)
        return [false, (...props) => mergeProps(...props)];
    function updateStart(fn) {
        start.current = fn(start.current);
    }
    function registerAbortSignal(signal) {
        signal.addEventListener('abort', () => dragEndRef.current(null, true));
    }
    function deltaFromStart(e) {
        return subtract(fromClientXY(e), start.current);
    }
    function dragStart(e, el) {
        setDragging(true);
        // By using pointerCapture we don't have to attach any events on the window, and can guarantee
        // that all events following the start of the drag will be sent to this element.
        pointerCaptureEl.current = el;
        el.setPointerCapture(e.pointerId);
        el.setAttribute('data-pointer-capture', '');
        document.body.classList.add(draggingBody);
        if (initialTextInput.current && isFocusWithin(initialTextInput.current)) {
            blurActiveElement();
        }
        // Once a drag starts, we want to make sure a pointercancel event cannot fire, so we add a
        // touchmove event listener to the document. React doesn't offer a way to do this in a
        // non-passive way so we have to resort to `addEventListener`.
        if (e.pointerType !== 'mouse') {
            document.addEventListener('touchmove', preventDefault, { passive: false });
        }
        ctx.onDragStart?.(e, { registerAbortSignal });
        // Call `onDrag` now, since we may have a delta beyond the `deadZone`.
        // It can simplify the caller's logic even when the delta is 0.
        const delta = deltaFromStart(e);
        ctx.onDrag?.(Object.assign(e, { delta }), { updateStart });
    }
    // This will only ever be called with a null event when cancelled is true, but it isn't
    // worth trying to encode with type overloads because it doesn't actually help typescript
    // infer the type of the event in the function body.
    function dragEnd(e, cancelled) {
        if (e && e.pointerId !== pointerId.current)
            return;
        // the drag never started, reset our state and don't call onDragEnd
        if (cancelled && pendingPointerCaptureEl.current) {
            pendingPointerCaptureEl.current = null;
            pointerId.current = null;
            return;
        }
        // The drag may have pointer-captured to a different element, so we need to look at
        // the element the event came from, not the element the event listener is attached to.
        pointerCaptureEl.current?.removeAttribute('data-pointer-capture');
        pointerCaptureEl.current = null;
        document.removeEventListener('touchmove', preventDefault);
        document.body.classList.remove(draggingBody);
        initialTextInput.current = null;
        if (!cancelled)
            window.getSelection()?.empty();
        setDragging(false);
        pointerId.current = null;
        if (cancelled && !e) {
            ctx.onDragEnd?.({ cancelled });
        }
        else {
            const delta = deltaFromStart(e);
            ctx.onDragEnd?.(Object.assign(e, { delta, cancelled }));
        }
    }
    dragEndRef.current = dragEnd;
    function shouldCancelDrag(e) {
        if (e.buttons === 0) {
            // We didn't catch the pointerup event (maybe the pointerdown opened an element beneath
            // the cursor), so we should cancel the drag.
            return true;
        }
        if (e.pointerType !== 'mouse' &&
            performance.now() - pointerDownStartTime.current > TOUCH_TIMEOUT) {
            // For touch/pen inputs, we don't allow the drag to start at all if the TOUCH_TIMEOUT
            // has elapsed. See the `TOUCH_TIMEOUT` jsdoc for more info.
            return true;
        }
        if (containsSelectedText(e.currentTarget)) {
            // If the user selects text while in the dead zone, we cancel the drag. It's a little awkward
            // to see the text selection disappear while dragging and then reappear when the drag ends,
            // so we just cancel the drag instead. UI text should typically be user-select: none anyway.
            return true;
        }
        if (isTextInputWithSelection(e.target)) {
            // If the user selects text in an input while in the dead zone, we cancel the drag.
            return true;
        }
        return false;
    }
    // Returns true if the caller should return early (because they are still in the dead zone),
    // false if the caller should continue (they have left the dead zone).
    function checkAndHandleDeadZone(e) {
        if (!ctx.deadZone || !pendingPointerCaptureEl.current)
            return false;
        // for pointerleave events, the pointer may have traveled further than DEAD_ZONE before firing,
        // but the pointerleave fired before the pointermove. In that case, we want to start the drag
        // DEAD_ZONE px away, rather than at the point the pointer left the element.
        const isPointerMove = e.type === 'pointermove';
        const delta = deltaFromStart(e);
        const magnitude$1 = magnitude(delta);
        const deadZone = getDeadZone(e.pointerType);
        const isWithinDeadZone = magnitude$1 < deadZone;
        if (isPointerMove && isWithinDeadZone)
            return true;
        if (shouldCancelDrag(e)) {
            dragEnd(e, true);
            return true;
        }
        let exitBox = isWithinDeadZone
            ? e.currentTarget.getBoundingClientRect()
            : null;
        // For text inputs, require both dead zone exit AND leaving the text input element
        if (initialTextInput.current) {
            const currentElement = elementUnderMouse(e);
            if (!currentElement)
                return false;
            const overTextInput = initialTextInput.current.contains(currentElement);
            if (overTextInput) {
                movementEventsOutsideTextInput.current = 0;
            }
            else {
                ++movementEventsOutsideTextInput.current;
            }
            let stillInDeadZone = false;
            if (movementEventsOutsideTextInput.current < MOVEMENT_EVENTS_OUTSIDE_TEXT_INPUT) {
                stillInDeadZone = true;
            }
            if (!stillInDeadZone) {
                // Use the same dead zone for exiting the text input that we use for starting the drag.
                const dist = distanceToElement(fromClientXY(e), initialTextInput.current);
                stillInDeadZone = dist < deadZone;
            }
            if (stillInDeadZone) {
                // If this is a pointerleave event, then cancel the drag outright, otherwise we can run
                // into weird situations where you pointer back into the draggable area and a drag starts
                if (!isPointerMove)
                    dragEnd(e, true);
                return true;
            }
            exitBox = expandRect(initialTextInput.current.getBoundingClientRect(), deadZone);
        }
        let exitPoint$1 = start.current;
        if (exitBox) {
            exitPoint$1 = exitPoint(start.current, fromClientXY(e), exitBox);
            // vec.exitPoint returns null if the start/end points did not actually exit the element. This
            // can happen on touch devices where pointerleave is fired when the pointer is lifted off the
            // screen as well as when the pointer moves out of the bounding box.
            if (!exitPoint$1)
                return true;
        }
        if (!isPointerMove || distance(start.current, exitPoint$1) > deadZone) {
            // This is a pointerleave event, but the pointer may have overshot the edge of the element by
            // a bit. In this case, we want the starting point of the drag to be the point at which the
            // pointer exited the element.
            start.current = exitPoint$1;
        }
        else {
            // Update our start position to the point that's `DEAD_ZONE` px away from the current point
            // along the delta vector. pointermove events are not instantaneous, so the pointer may have
            // traveled a bit further than DEAD_ZONE px before firing.
            const pointDragZonePxAway = normalize(delta, getDeadZone(e.pointerType));
            start.current = add(start.current, pointDragZonePxAway);
        }
        dragStart(e, pendingPointerCaptureEl.current);
        pendingPointerCaptureEl.current = null;
        return true;
    }
    const dragProps = {
        onPointerDown(e) {
            if (isPortaledEvent(e))
                return;
            // On touch devices we may receive multiple pointer down events
            // before we receive a pointer up, so only react to the first one.
            if (pointerId.current != null)
                return;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            if (!ctx.customButtonCheck && e.button !== 0 /* MouseButton.LEFT */)
                return;
            // temporary fix for old scrubbable inputs. we can't call stopPropagation on their pointerdown
            // because they rely on a non-capturing pointerdown listener on the document
            if (e.target.hasAttribute('data-temporary-fpl-no-drag'))
                return;
            const result = ctx.onBeforeDrag?.(e);
            if (result === false)
                return;
            e.stopPropagation();
            start.current = fromClientXY(e);
            pointerId.current = e.pointerId;
            pointerDownStartTime.current = performance.now();
            movementEventsOutsideTextInput.current = 0;
            const el = result instanceof Element ? result : e.currentTarget;
            if (ctx.deadZone &&
                !targetIsCurrent(e) &&
                !e.target.hasAttribute('data-no-dead-zone')) {
                initialTextInput.current = getFurthestEditableAncestorOrSelf(e.target);
                pendingPointerCaptureEl.current = el;
                return;
            }
            // clear the selection at drag start so it doesn't follow us around or reappear at drag end
            window.getSelection()?.empty();
            dragStart(e, el);
        },
        onPointerMove(e) {
            // If this isn't the same pointer that started the drag, ignore it.
            // This also catches when `pointerId.current` is null (and we aren't dragging yet).
            if (e.pointerId !== pointerId.current)
                return;
            if (checkAndHandleDeadZone(e))
                return;
            const delta = deltaFromStart(e);
            ctx.onDrag?.(Object.assign(e, { delta }), { updateStart });
        },
        // An edge case, but if the pointer leaves the element while we're waiting for it to cross
        // the dead zone, we should start the drag at that point. It feels better than cancelling.
        onPointerLeave(e) {
            if (e.pointerId !== pointerId.current)
                return;
            checkAndHandleDeadZone(e);
        },
        onPointerUp(e) {
            const cancelled = !!(ctx.deadZone && pendingPointerCaptureEl.current);
            dragEnd(e, cancelled);
        },
        // On Chrome we don't always receive a pointerup event, but we do always receive a
        // lostpointercapture event. We listen to both though because some browsers (like Safari) don't
        // include clientX/Y in lostpointercapture events, but _do_ reliably send pointerup. Attaching
        // both listeners means we can call onDragEnd with the correct delta across browsers.
        // See https://bugs.chromium.org/p/chromium/issues/detail?id=1515939
        onLostPointerCapture(e) {
            // Pointer capture is implicit for touch events - this means that when we call
            // `setPointerCapture` above, we're implicitly cancelling the current pointer capture to
            // add our own. So specifically for touch events, we can safely ignore the lostpointercapture
            // event. We'll still receive a pointerup event when the finger is lifted which ends the drag.
            if (e.pointerType === 'touch')
                return;
            // If we're losing pointer capture on the same element that has pointer lock, we can safely
            // ignore this event. Sometimes lostpointercapture is fired if the pointer is moving when
            // pointerdown is fired, but we don't want to cancel the drag in that case.
            if (document.pointerLockElement === e.target)
                return;
            const cancelled = !!(ctx.deadZone && pendingPointerCaptureEl.current);
            dragEnd(e, cancelled);
        },
        onPointerCancel(e) {
            dragEnd(e, true);
        },
        // TODO: we can remove this once we've migrated all our draggable modals to use the fpl version,
        // but until then we need to make sure that dragging a child of an old draggable modal doesn't
        // drag the modal instead.
        onMouseDown: stopPropagation,
        className: clsx(base, { [dragging]: dragging$1 }),
        ...PREVENT_EVENT_CAPTURE,
    };
    return [dragging$1, (...props) => mergeProps(dragProps, ...props)];
}

export { useCancellableDrag, useDrag };
//# sourceMappingURL=use-drag.js.map
